<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">Create Workspace</h1>
        <p class="console-page__placeholder">
          Provision a repository workspace for console operations using GitHub or an uploaded archive.
        </p>
      </div>

      <div class="console-page__actions">
        <router-link :to="{ name: 'ConsoleWorkspaces' }" class="button button--ghost">
          Back to Workspaces
        </router-link>
      </div>
    </div>

    <section class="console-panel">
      <div class="console-signal-strip workspace-create__summary-strip">
        <button
          type="button"
          class="console-signal console-signal--button"
          :class="sourceMode === 'github' ? 'console-signal--active' : 'console-signal--pending'"
          aria-controls="workspace-create-source-section"
          @click="openSourceSection"
        >
          <span class="console-signal__label">Source</span>
          <span class="console-signal__value workspace-create__signal-value">{{ sourceModeLabel }}</span>
          <span class="console-signal__note">{{ sourceModeNote }}</span>
        </button>
        <button
          type="button"
          class="console-signal console-signal--button"
          :class="sourceMode === 'github' && visibility === 'private' ? 'console-signal--attention' : 'console-signal--ready'"
          aria-controls="workspace-create-source-section"
          @click="openAccessSection"
        >
          <span class="console-signal__label">Access</span>
          <span class="console-signal__value workspace-create__signal-value">{{ accessLabel }}</span>
          <span class="console-signal__note">{{ accessNote }}</span>
        </button>
        <button
          type="button"
          class="console-signal console-signal--button console-signal--pending"
          aria-controls="workspace-create-contract-section"
          @click="openContractSection"
        >
          <span class="console-signal__label">Assembly</span>
          <span class="console-signal__value workspace-create__signal-value">Async</span>
          <span class="console-signal__note">After create, the console opens workspace detail while assembly continues in the background.</span>
        </button>
      </div>

      <details id="workspace-create-workspace-section" class="console-module" open>
        <summary class="console-module__summary">Workspace</summary>
        <div class="workspace-create__stack">
          <label class="console-filter">
            <span class="console-filter__label">Name</span>
            <input
              v-model="workspaceName"
              type="text"
              class="form-input"
              placeholder="e.g. 86proof-site"
            />
          </label>

          <label class="console-filter">
            <span class="console-filter__label">Description</span>
            <textarea
              v-model="workspaceDescription"
              class="form-input workspace-create__textarea workspace-create__textarea--short"
              rows="3"
              placeholder="Optional operator-facing description of the workspace."
            />
          </label>
        </div>
      </details>

      <details id="workspace-create-source-section" ref="sourceSection" class="console-module" open>
        <summary class="console-module__summary">Source</summary>
        <div class="workspace-create__stack">
          <div class="workspace-create__mode-toggle" role="radiogroup" aria-label="Workspace source">
            <label class="workspace-create__mode-option">
              <input v-model="sourceMode" type="radio" value="github">
              <span>GitHub Repository</span>
            </label>
            <label class="workspace-create__mode-option">
              <input v-model="sourceMode" type="radio" value="upload">
              <span>S3 Archive Upload</span>
            </label>
          </div>

          <template v-if="sourceMode === 'github'">
            <label class="console-filter">
              <span class="console-filter__label">Repository URL</span>
              <input
                ref="repoUrlInput"
                v-model="repoUrl"
                type="text"
                class="form-input workspace-create__mono"
                placeholder="https://github.com/owner/repo"
              />
            </label>

            <p v-if="repoUrlError" class="workspace-create__error">{{ repoUrlError }}</p>

            <label class="console-filter">
              <span class="console-filter__label">Branch</span>
              <input
                v-model="branch"
                type="text"
                class="form-input"
                placeholder="main"
              />
            </label>

            <p v-if="branchError" class="workspace-create__error">{{ branchError }}</p>

            <div class="workspace-create__mode-toggle" role="radiogroup" aria-label="Repository visibility">
              <label class="workspace-create__mode-option">
                <input ref="publicVisibilityInput" v-model="visibility" type="radio" value="public">
                <span>Public</span>
              </label>
              <label class="workspace-create__mode-option">
                <input ref="privateVisibilityInput" v-model="visibility" type="radio" value="private">
                <span>Private (PAT)</span>
              </label>
            </div>

            <p class="workspace-create__hint">
              Public repositories clone without stored credentials. Private repositories require a GitHub personal access token for assembly.
            </p>

            <label v-if="visibility === 'private'" class="console-filter">
              <span class="console-filter__label">GitHub Token</span>
              <input
                ref="githubTokenInput"
                v-model="githubToken"
                type="password"
                class="form-input workspace-create__mono"
                placeholder="ghp_..."
              />
            </label>
          </template>

          <template v-else>
            <label class="console-filter">
              <span class="console-filter__label">Archive File</span>
              <input
                ref="archiveInput"
                type="file"
                class="form-input workspace-create__file-input"
                accept=".zip,.tar.gz,.tgz"
                @change="onArchiveSelected"
              >
            </label>

            <p class="workspace-create__hint">
              Upload a `.zip`, `.tar.gz`, or `.tgz` archive. The console assumes the backend exposes the archive upload endpoint for workspace assembly.
            </p>

            <p v-if="selectedArchiveName" class="workspace-create__hint">
              Selected archive: <span class="workspace-create__mono">{{ selectedArchiveName }}</span>
            </p>
          </template>
        </div>
      </details>

      <details id="workspace-create-contract-section" ref="contractSection" class="console-module">
        <summary class="console-module__summary">Create Contract</summary>
        <dl class="console-metric-list">
          <div class="console-metric">
            <dt>Source</dt>
            <dd>{{ sourceModeLabel }}</dd>
          </div>
          <div class="console-metric">
            <dt>Workspace Type</dt>
            <dd>{{ workspaceTypeLabel }}</dd>
          </div>
          <div class="console-metric">
            <dt>Assembly</dt>
            <dd>Background handoff to detail page</dd>
          </div>
        </dl>
      </details>

      <div v-if="submitError" class="alert alert--error">
        {{ submitError }}
      </div>

      <div v-if="submitMessage" class="workspace-create__message">
        {{ submitMessage }}
      </div>

      <div class="button-row">
        <button
          type="button"
          class="button button--primary"
          :disabled="!canSubmit"
          @click="submitWorkspace"
        >
          {{ submitting ? 'Creating...' : 'Create Workspace' }}
        </button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useWorkspaceCreation } from '@/composables/useWorkspaceCreation.js'
import { useFrontendLogger } from '@/services/FrontendLogger'

const router = useRouter()
const { createGithubWorkspace, createUploadWorkspace } = useWorkspaceCreation()
const logger = useFrontendLogger().child('[ConsoleWorkspaceCreate]')

const workspaceName = ref('')
const workspaceDescription = ref('')
const sourceMode = ref('github')
const repoUrl = ref('')
const branch = ref('')
const visibility = ref('public')
const githubToken = ref('')
const selectedArchive = ref(null)
const submitting = ref(false)
const submitError = ref('')
const submitMessage = ref('')
const sourceSection = ref(null)
const contractSection = ref(null)
const repoUrlInput = ref(null)
const publicVisibilityInput = ref(null)
const privateVisibilityInput = ref(null)
const githubTokenInput = ref(null)
const archiveInput = ref(null)

const GITHUB_URL_RE = /^https:\/\/github\.com\/[\w.-]+\/[\w.-]+(\.git)?$/
const GITHUB_SSH_RE = /^git@github\.com:[\w.-]+\/[\w.-]+(\.git)?$/

const sourceModeLabel = computed(() => {
  return sourceMode.value === 'github' ? 'GitHub' : 'Archive Upload'
})

const sourceModeNote = computed(() => {
  return sourceMode.value === 'github'
    ? 'Clone a repository and create a managed workspace branch.'
    : 'Upload an archive for S3-backed workspace assembly.'
})

const accessLabel = computed(() => {
  if (sourceMode.value === 'upload') return 'Archive'
  return visibility.value === 'private' ? 'Private' : 'Public'
})

const accessNote = computed(() => {
  if (sourceMode.value === 'upload') {
    return 'Archive uploads bypass repository credential setup.'
  }
  return visibility.value === 'private'
    ? 'Private repositories require a GitHub token.'
    : 'Public repositories do not require stored credentials.'
})

const workspaceTypeLabel = computed(() => {
  if (sourceMode.value === 'upload') return 'upload'
  return visibility.value === 'private' ? 'private-github' : 'public-github'
})

const selectedArchiveName = computed(() => selectedArchive.value?.name || '')

const repoUrlError = computed(() => {
  if (sourceMode.value !== 'github') return ''
  const trimmed = repoUrl.value.trim()
  if (!trimmed) return ''
  if (GITHUB_URL_RE.test(trimmed) || GITHUB_SSH_RE.test(trimmed)) return ''
  return 'Expected https://github.com/owner/repo or git@github.com:owner/repo.'
})

const branchError = computed(() => {
  if (sourceMode.value !== 'github') return ''
  if (!repoUrl.value.trim()) return ''
  if (branch.value.trim()) return ''
  return 'Branch is required. Enter the branch to clone, for example main.'
})

const canSubmit = computed(() => {
  if (submitting.value) return false
  if (!workspaceName.value.trim()) return false

  if (sourceMode.value === 'github') {
    if (!repoUrl.value.trim()) return false
    if (repoUrlError.value) return false
    if (branchError.value) return false
    if (visibility.value === 'private' && !githubToken.value.trim()) return false
    return true
  }

  return Boolean(selectedArchive.value)
})

function onArchiveSelected(event) {
  const file = event.target.files?.[0]
  selectedArchive.value = file || null
}

function openSection(sectionRef, focusRef) {
  sectionRef.value?.setAttribute('open', '')
  sectionRef.value?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
  focusRef?.value?.focus?.()
}

function openSourceSection() {
  openSection(sourceSection, sourceMode.value === 'github' ? repoUrlInput : archiveInput)
}

function openAccessSection() {
  if (sourceMode.value === 'upload') {
    openSection(sourceSection, archiveInput)
    return
  }

  if (visibility.value === 'private') {
    openSection(sourceSection, githubTokenInput.value ? githubTokenInput : privateVisibilityInput)
    return
  }

  openSection(sourceSection, publicVisibilityInput)
}

function openContractSection() {
  openSection(contractSection, null)
}

async function submitWorkspace() {
  if (!canSubmit.value) return

  submitting.value = true
  submitError.value = ''
  submitMessage.value = ''

  try {
    const data = sourceMode.value === 'github'
      ? await createGithubWorkspace({
          repoUrl: repoUrl.value.trim(),
          branch: branch.value.trim(),
          visibility: visibility.value,
          githubToken: githubToken.value.trim(),
          name: workspaceName.value.trim(),
          description: workspaceDescription.value.trim(),
        })
      : await createUploadWorkspace({
          name: workspaceName.value.trim(),
          description: workspaceDescription.value.trim(),
          file: selectedArchive.value,
        })

    const workspaceId = data.workspaceId ?? data.id
    submitMessage.value = 'Workspace created. Opening detail view...'
    await router.push({ name: 'ConsoleWorkspaceDetail', params: { id: workspaceId } })
  } catch (createError) {
    submitError.value = createError.message || 'Failed to create workspace.'
    logger.warn('Failed to create workspace', createError)
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.workspace-create__summary-strip {
  margin-bottom: 16px;
}

.workspace-create__stack {
  display: grid;
  gap: 16px;
}

.workspace-create__signal-value {
  font-size: clamp(1.15rem, 2.3vw, 1.5rem);
  line-height: 1.05;
}

.workspace-create__mode-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.workspace-create__mode-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 10px 12px;
  border: 1px solid var(--shell-border);
  background: var(--shell-surface-strong);
  color: var(--shell-text);
  font-size: 0.88rem;
}

.workspace-create__mono {
  font-family: var(--shell-mono-font);
}

.workspace-create__textarea {
  resize: vertical;
  line-height: 1.6;
}

.workspace-create__textarea--short {
  min-height: 88px;
}

.workspace-create__file-input {
  padding-block: 10px;
}

.workspace-create__hint,
.workspace-create__message {
  margin: 0;
  color: var(--shell-text-muted);
  line-height: 1.6;
}

.workspace-create__error {
  margin: 0;
  color: #9c3030;
  font-size: 0.84rem;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .workspace-create__mode-toggle {
    flex-direction: column;
  }
}
</style>

